import { ClientDestinationsModel } from '../../../api/hooks/useDestinations';

export interface ValidationError {
  key: string;
  params: Record<string, any>;
}

export function validateClientDestinationsModel(
  model: ClientDestinationsModel
): Map<string, Array<ValidationError>> {
  const errorsByDestinationId: Map<string, Array<ValidationError>> = new Map<
    string,
    Array<ValidationError>
  >();

  // Track all identifiers across all destinations to check for global duplicates
  const allIdentifiers: Map<
    string,
    Array<{ ouName: string; destinationId: string }>
  > = new Map();

  model.organizationalUnitDtos.forEach((orgUnit) => {
    orgUnit.destinationDtos.forEach((destination) => {
      const errors: Array<{ key: string; params: Record<string, any> }> = [];
      const identifiers = destination.publicServiceTypeDto.map(
        (pst) => pst.identifier
      );
      const uniqueIdentifiers = new Set(identifiers);

      if (!orgUnit.name.trim()) {
        errors.push({
          key: 'destinations.validation.empty_name',
          params: {
            ouName: orgUnit.nameInFitConnect,
            destinationId: destination.destinationId
          }
        });
      }

      if (identifiers.length !== uniqueIdentifiers.size) {
        const duplicates = identifiers.filter(
          (item, index) => identifiers.indexOf(item) !== index
        );
        errors.push({
          key: 'destinations.validation.duplicate_identifiers',
          params: {
            ouName: orgUnit.name,
            destinationId: destination.destinationId,
            duplicateIdentifiers: duplicates.join(', ')
          }
        });
      }

      // Track identifiers for global duplicate check
      destination.publicServiceTypeDto.forEach((pst) => {
        if (!allIdentifiers.has(pst.identifier)) {
          allIdentifiers.set(pst.identifier, []);
        }

        allIdentifiers.get(pst.identifier)?.push({
          ouName: orgUnit.name,
          destinationId: destination.destinationId
        });

        pst.submissionSchemaDtos.forEach((schema) => {
          if (!schema.schemaUri.startsWith('https://')) {
            errors.push({
              key: 'destinations.validation.invalid_uri_protocol',
              params: {
                ouName: orgUnit.name,
                destinationId: destination.destinationId,
                identifier: pst.identifier,
                schemaUri: schema.schemaUri
              }
            });
          }

          if (schema.mimeType !== 'application/json') {
            errors.push({
              key: 'destinations.validation.invalid_mime_type',
              params: {
                ouName: orgUnit.name,
                destinationId: destination.destinationId,
                identifier: pst.identifier,
                mimeType: schema.mimeType
              }
            });
          }
        });
      });
      if (errors.length > 0) {
        errorsByDestinationId.set(destination.destinationId, errors);
      }
    });
  });

  // Check for duplicate identifiers across all destinations
  allIdentifiers.forEach((occurrences, identifier) => {
    if (occurrences.length > 1) {
      // There are duplicates of this identifier across destinations
      occurrences.forEach(({ ouName, destinationId }) => {
        const errors = errorsByDestinationId.get(destinationId) || [];
        errors.push({
          key: 'destinations.validation.global_duplicate_identifier',
          params: {
            ouName,
            destinationId,
            identifier,
            occurrencesCount: occurrences.length,
            otherLocations: occurrences
              .filter((loc) => loc.destinationId !== destinationId)
              .map((loc) => `${loc.destinationId}`)
              .join(', ')
          }
        });
        errorsByDestinationId.set(destinationId, errors);
      });
    }
  });

  return errorsByDestinationId;
}
